/* eslint-disable camelcase */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { ReactComponent as ArrowBackIcon } from 'assets/svg/arrow-back.svg';
import { ReactComponent as InfoIcon } from 'assets/svg/info-circle.svg';
import { ReactComponent as ArrowDown } from 'assets/svg/dropdown-arrow.svg';
import SectionHeader from 'components/SectionHeader';
import Button from 'components/Button';
import theme from 'assets/theme/mainTheme';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import { HA_ROUTES } from 'routing/constants';
import { ROLES_TO_SELECT } from 'constants.js';
import { useAuth0 } from '@auth0/auth0-react';
import useMPA from 'hooks/useMPA';
import useFetch from 'hooks/useFetch';
import apiRoutes from 'config/apiRoutes';

import {
    FormWrapper,
    Section,
    Title,
    CheckboxWrapper,
    Text,
    RoleDropdownWrapper,
    Role,
    Dropdown,
    DropdownItem,
} from './CreateNewUserHA.style';
import { ClassificationTagDropdown } from '../../components/ClassificationTagDropdown/ClassificationTagDropdown';

const CreateNewUserHAView = ({
    setFirstName,
    isFirstNameValid,
    setLastName,
    isLastNameValid,
    setEmailAddress,
    isEmailAddressValid,
    role,
    setRole,
    setIsRoleDropdownVisible,
    isRoleDropdownVisible,
    setMnemonic,
    isMnemonicValid,
    isLMSSelected,
    setIsLMSSelected,
    createUser,
    isButtonDisabled,
    isLMSEnabled,
    setPassword,
    setClassificationTags,
    classificationTags,
}) => {
    const roleDropdownRef = useRef(null);
    const history = useHistory();
    const { isCurrentUserMPA } = useMPA();
    const { getAccessTokenSilently, logout } = useAuth0();
    const request = useFetch(getAccessTokenSilently, logout);
    const [mpaLMSEnabled, setMpaLMSEnabled] = useState(false);
    const [enableSetPassword, setEnableSetPassword] = useState(false);

    const handleClickOutside = (e) => {
        if (
            roleDropdownRef.current &&
            roleDropdownRef.current.contains(e.target)
        ) {
            return;
        }
        setIsRoleDropdownVisible(false);
    };

    useEffect(() => {
        if (isRoleDropdownVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isRoleDropdownVisible]);

    useEffect(() => {
        if (isCurrentUserMPA) {
            request(
                apiRoutes.get.tenantOrganization,
                'GET',
                (res) => setMpaLMSEnabled(res?.is_lms_enabled),
                () => setMpaLMSEnabled(false)
            );
        }
    }, [isCurrentUserMPA]);

    return (
        <>
            <SectionHeader
                goBack={{
                    url: HA_ROUTES.users,
                    label() {
                        return (
                            <>
                                <ArrowBackIcon stroke={theme.color.textLabel} />
                                <div style={{ color: theme.color.primary }}>
                                    Back
                                </div>
                            </>
                        );
                    },
                }}
                title="Add User"
                customStyles={{ marginBottom: '16px' }}
            />
            <FormWrapper>
                <Section grid>
                    <Title>Complete new User details</Title>
                    <Input
                        placeholder="First name"
                        onChange={(e) => setFirstName(e.target.value)}
                        invalid={!isFirstNameValid()}
                    />
                    <Input
                        placeholder="Last name"
                        onChange={(e) => setLastName(e.target.value)}
                        invalid={!isLastNameValid()}
                    />
                    <Input
                        placeholder="E-mail address"
                        onChange={(e) => setEmailAddress(e.target.value)}
                        invalid={!isEmailAddressValid()}
                    />
                    {/* <Input
                        placeholder="Password (will be auto-generated if left blank)"
                        onChange={(e) => setPassword(e.target.value)}
                        type="password"
                    /> */}
                    <RoleDropdownWrapper ref={roleDropdownRef}>
                        <Role
                            onClick={() =>
                                setIsRoleDropdownVisible(!isRoleDropdownVisible)
                            }
                            style={{ width: '100%' }}
                        >
                            {ROLES_TO_SELECT[role]
                                ? ROLES_TO_SELECT[role]
                                : 'User Type'}
                            <ArrowDown />
                        </Role>
                        {isRoleDropdownVisible && (
                            <Dropdown style={{ width: '100%' }}>
                                {Object.keys(ROLES_TO_SELECT).map((option) => (
                                    <DropdownItem
                                        key={option}
                                        isSelected={role === option}
                                        onClick={() => {
                                            setIsRoleDropdownVisible(false);
                                            if (role === option) {
                                                setRole(undefined);
                                            } else {
                                                setRole(option);
                                            }
                                        }}
                                    >
                                        {ROLES_TO_SELECT[option]}
                                    </DropdownItem>
                                ))}
                            </Dropdown>
                        )}
                    </RoleDropdownWrapper>
                </Section>

                <Section grid borderTop>
                    <div>
                        <ClassificationTagDropdown
                            currentlySavedTags={classificationTags}
                            setClassificationTags={setClassificationTags}
                            localMode
                        />
                    </div>
                </Section>

                <Section borderTop grid>
                    <Title>Password (Optional)</Title>

                    <CheckboxWrapper>
                        <Checkbox
                            isSelected={enableSetPassword}
                            toggleSelected={() =>
                                setEnableSetPassword(!enableSetPassword)
                            }
                        />
                        <Text>Set account password</Text>
                    </CheckboxWrapper>

                    <Input
                        disabled={!enableSetPassword}
                        placeholder="Password "
                        onChange={(e) => setPassword(e.target.value)}
                        type="password"
                    />
                </Section>

                <Section borderTop grid>
                    <Title>
                        <InfoIcon fill={theme.color.textLight} /> Unique ID used
                        to connect User to imported performance data.
                    </Title>
                    <Input
                        placeholder="User ID or Mnemonic"
                        onChange={(e) => setMnemonic(e.target.value)}
                        invalid={!isMnemonicValid()}
                    />
                </Section>
                <Section borderTop>
                    <CheckboxWrapper
                        isLMSEnabled={isLMSEnabled || mpaLMSEnabled}
                    >
                        <Checkbox
                            isSelected={isLMSSelected}
                            disabled={!isLMSEnabled && !mpaLMSEnabled}
                            toggleSelected={() =>
                                setIsLMSSelected(!isLMSSelected)
                            }
                        />
                        <Text disabled={!isLMSEnabled && !mpaLMSEnabled}>
                            Create User in MedPower LMS
                        </Text>
                    </CheckboxWrapper>
                </Section>
                <Section borderTop>
                    <Button
                        primaryDark
                        customStyles={{ marginRight: '16px' }}
                        onClick={createUser}
                        disabled={isButtonDisabled}
                    >
                        Create User
                    </Button>
                    <Button primary onClick={() => history.push('ha-users')}>
                        Cancel
                    </Button>
                </Section>
            </FormWrapper>
        </>
    );
};

CreateNewUserHAView.propTypes = {
    setFirstName: PropTypes.func.isRequired,
    setPassword: PropTypes.func.isRequired,
    isFirstNameValid: PropTypes.func.isRequired,
    setLastName: PropTypes.func.isRequired,
    isLastNameValid: PropTypes.func.isRequired,
    setEmailAddress: PropTypes.func.isRequired,
    isEmailAddressValid: PropTypes.func.isRequired,
    setIsRoleDropdownVisible: PropTypes.func.isRequired,
    role: PropTypes.string,
    setRole: PropTypes.func.isRequired,
    isRoleDropdownVisible: PropTypes.bool.isRequired,
    setMnemonic: PropTypes.func.isRequired,
    isMnemonicValid: PropTypes.func.isRequired,
    isLMSSelected: PropTypes.bool.isRequired,
    setIsLMSSelected: PropTypes.func.isRequired,
    createUser: PropTypes.func.isRequired,
    isButtonDisabled: PropTypes.bool.isRequired,
    isLMSEnabled: PropTypes.bool.isRequired,
    setClassificationTags: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    classificationTags: PropTypes.array.isRequired,
};

CreateNewUserHAView.defaultProps = {
    role: undefined,
};
/* eslint-enable camelcase */

export default CreateNewUserHAView;
